/**
 * Usage and cost (PLAN §9.6): a month's cloud production hours and their
 * estimated cost, the totals and per production (from the metered task
 * runs). Production-hours count a redundant production once; task-hours
 * count each of its tasks. The cost is an estimate at the task sizes' list
 * prices — billing starts from these reports.
 */
import type { CloudUsageResponseWire } from '@acp/api-contract/cloudProductions.js';
import { RefreshCwIcon } from 'lucide-react';
import { usageByProduction, usd } from '../../lib/admin/view.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type UsageReportProps = {
  readonly months: readonly string[];
  readonly month: string;
  readonly usage: CloudUsageResponseWire | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onMonthChange: (month: string) => void;
  readonly onRetry: () => void;
};

function Stat({ label, value, testId }: { readonly label: string; readonly value: string; readonly testId: string }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

export function UsageReport({ months, month, usage, loading, error, onMonthChange, onRetry }: UsageReportProps) {
  const rows = usage === null ? [] : usageByProduction(usage);
  return (
    <Card data-testid="usage-report">
      <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Usage and cost</CardTitle>
          <CardDescription>Cloud production hours and their estimated cost, per month (UTC).</CardDescription>
        </div>
        <div className="grid gap-1">
          <Label htmlFor="usage-month">Month</Label>
          <NativeSelect id="usage-month" value={month} onChange={(e) => onMonthChange(e.target.value)}>
            {months.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </NativeSelect>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load usage: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {loading && usage === null ? <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p> : null}
        {usage === null ? null : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <Stat label="Production-hours" value={usage.productionHours.toFixed(2)} testId="usage-production-hours" />
              <Stat label="Task-hours" value={usage.taskHours.toFixed(2)} testId="usage-task-hours" />
              <Stat label="Estimated cost" value={usd(usage.costUsd)} testId="usage-cost" />
            </div>
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="usage-empty">
                No cloud productions ran in {usage.month}.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Production</TableHead>
                    <TableHead className="w-24">Task runs</TableHead>
                    <TableHead className="w-28">Task-hours</TableHead>
                    <TableHead className="w-28">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.productionId} data-testid="usage-row">
                      <TableCell className="font-mono text-xs">{r.productionId}</TableCell>
                      <TableCell className="tabular-nums">{r.runs}</TableCell>
                      <TableCell className="tabular-nums">{r.hours.toFixed(2)}</TableCell>
                      <TableCell className="tabular-nums">{usd(r.costUsd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
